import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle, ZoomIn, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { fetchProduct, fetchStoreInfo } from '../services/api';

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [product, setProduct] = useState(null);
  const [storeInfo, setStoreInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [isZoomed, setIsZoomed] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [productData, storeData] = await Promise.all([
          fetchProduct(parseInt(id)),
          fetchStoreInfo()
        ]);
        setProduct(productData);
        setStoreInfo(storeData);
      } catch (error) {
        console.error('Failed to load product:', error);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      `Merhaba, ${product?.name} hakkında bilgi almak istiyorum.`
    );
    window.open(`https://wa.me/${storeInfo.whatsapp}?text=${message}`, '_blank');
  };

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePosition({ x, y });
  };

  const getCategoryName = (categoryId) => {
    const categoryNames = {
      set: { tr: 'Setler', en: 'Sets' },
      zincir: { tr: 'Zincirler', en: 'Chains' },
      kolye: { tr: 'Kolyeler', en: 'Necklaces' },
    };
    return categoryNames[categoryId]?.[language] || categoryId;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fffef2]">
        <Header />
        <main className="pt-32 pb-16">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div className="aspect-square bg-[#f6f5e8] animate-pulse" />
              <div className="space-y-6">
                <div className="h-8 bg-[#f6f5e8] w-1/3 animate-pulse" />
                <div className="h-12 bg-[#f6f5e8] w-2/3 animate-pulse" />
                <div className="h-24 bg-[#f6f5e8] animate-pulse" />
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#fffef2]">
        <Header />
        <main className="pt-32 pb-16">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
            <h1 className="text-2xl text-[#333333] mb-4">
              {language === 'tr' ? 'Ürün bulunamadı' : 'Product not found'}
            </h1>
            <Link
              to="/collections"
              className="inline-flex items-center gap-2 text-[#b8860b] hover:text-[#8b7355] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {language === 'tr' ? 'Koleksiyonlara Dön' : 'Back to Collections'}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fffef2]">
      <Header />
      <main className="pt-32 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link to="/" className="text-[#666666] hover:text-[#333333] transition-colors">
                  {t('nav.home')}
                </Link>
              </li>
              <li className="text-[#bcbbb4]">/</li>
              <li>
                <Link to="/collections" className="text-[#666666] hover:text-[#333333] transition-colors">
                  {t('nav.collections')}
                </Link>
              </li>
              <li className="text-[#bcbbb4]">/</li>
              <li>
                <Link 
                  to={`/collections?category=${product.category}`} 
                  className="text-[#666666] hover:text-[#333333] transition-colors"
                >
                  {getCategoryName(product.category)}
                </Link>
              </li>
              <li className="text-[#bcbbb4]">/</li>
              <li className="text-[#333333]">{product.name}</li>
            </ol>
          </nav>

          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-[#666666] hover:text-[#333333] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'tr' ? 'Geri Dön' : 'Go Back'}
          </button>

          {/* Product Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Product Image with Zoom */}
            <div className="relative">
              <div
                className="relative aspect-square overflow-hidden bg-[#f6f5e8] cursor-zoom-in group"
                onMouseMove={handleMouseMove}
                onMouseEnter={() => setIsZoomed(true)}
                onMouseLeave={() => setIsZoomed(false)}
              >
                <img
                  src={product.image}
                  alt={product.name}
                  className={`w-full h-full object-cover transition-transform duration-300 ${
                    isZoomed ? 'scale-150' : 'scale-100'
                  }`}
                  style={
                    isZoomed
                      ? {
                          transformOrigin: `${mousePosition.x}% ${mousePosition.y}%`,
                        }
                      : {}
                  }
                />
                {/* Zoom indicator */}
                <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-2 flex items-center gap-2 text-sm text-[#666666] opacity-100 group-hover:opacity-0 transition-opacity">
                  <ZoomIn className="w-4 h-4" />
                  {language === 'tr' ? 'Yakınlaştırmak için üzerine gelin' : 'Hover to zoom'}
                </div>
              </div>

              {/* Fullscreen Image Button */}
              <button
                onClick={() => setIsZoomed('fullscreen')}
                className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-3 hover:bg-white transition-colors"
              >
                <ZoomIn className="w-5 h-5 text-[#333333]" />
              </button>
            </div>

            {/* Product Details */}
            <div className="lg:py-8">
              {/* Category */}
              <Link
                to={`/collections?category=${product.category}`}
                className="text-[#b8860b] text-sm font-medium tracking-widest uppercase mb-4 block hover:text-[#8b7355] transition-colors"
              >
                {getCategoryName(product.category)}
              </Link>

              {/* Product Name */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-[#333333] mb-6 tracking-wide">
                {product.name}
              </h1>

              {/* Karat Info */}
              <div className="flex items-center gap-4 mb-8 pb-8 border-b border-[#ebeade]">
                <span className="text-[#b8860b] text-lg font-medium">
                  {product.karat} {t('product.karat')}
                </span>
                <span className="text-[#bcbbb4]">|</span>
                <span className="text-[#666666] text-sm">
                  {language === 'tr' ? 'Saf Altın' : 'Pure Gold'}
                </span>
              </div>

              {/* Description */}
              <div className="mb-10">
                <h3 className="text-[#333333] text-lg font-medium mb-4">
                  {language === 'tr' ? 'Ürün Detayları' : 'Product Details'}
                </h3>
                <p className="text-[#666666] text-base leading-relaxed mb-4">
                  {product.description[language]}
                </p>
                <p className="text-[#666666] text-base leading-relaxed">
                  {language === 'tr'
                    ? 'Bu eşsiz parça, uzman kuyumcularımız tarafından en kaliteli malzemeler kullanılarak özenle işlenmiştir. Her detay mükemmellik anlayışımızı yansıtmaktadır.'
                    : 'This unique piece has been carefully crafted by our expert jewelers using the finest materials. Every detail reflects our commitment to excellence.'}
                </p>
              </div>

              {/* Features */}
              <div className="mb-10 pb-8 border-b border-[#ebeade]">
                <h3 className="text-[#333333] text-lg font-medium mb-4">
                  {language === 'tr' ? 'Özellikler' : 'Features'}
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-[#666666]">
                    <span className="w-1.5 h-1.5 bg-[#b8860b] rounded-full" />
                    {language === 'tr' ? `${product.karat} Ayar Altın` : `${product.karat} Karat Gold`}
                  </li>
                  <li className="flex items-center gap-3 text-[#666666]">
                    <span className="w-1.5 h-1.5 bg-[#b8860b] rounded-full" />
                    {language === 'tr' ? 'El İşçiliği' : 'Handcrafted'}
                  </li>
                  <li className="flex items-center gap-3 text-[#666666]">
                    <span className="w-1.5 h-1.5 bg-[#b8860b] rounded-full" />
                    {language === 'tr' ? 'Garanti Belgeli' : 'Certified Warranty'}
                  </li>
                </ul>
              </div>

              {/* WhatsApp CTA */}
              <div className="space-y-4">
                <p className="text-[#666666] text-sm">
                  {language === 'tr'
                    ? 'Fiyat bilgisi ve detaylar için bizimle iletişime geçin'
                    : 'Contact us for pricing and details'}
                </p>
                <button
                  onClick={handleWhatsApp}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-10 py-5 bg-[#25d366] text-white text-sm font-medium tracking-widest uppercase transition-all duration-300 hover:bg-[#128c7e]"
                >
                  <MessageCircle className="w-5 h-5" />
                  {t('product.contact')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Fullscreen Image Modal */}
      {isZoomed === 'fullscreen' && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setIsZoomed(false)}
        >
          <button
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"
            onClick={() => setIsZoomed(false)}
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={product.image}
            alt={product.name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProductPage;
